<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { devices_settings, devices_status } from 'src/stores';
	import { devices } from 'src/stores/lists/devices';
	import { devices_config } from 'src/configs/devices';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';
	import StatusBar from 'src/components/Lists/Devices/StatusBar.svelte';
	import IRAddDeviceModal from '../IRAddDeviceModal.svelte';
	import IRDeviceSettingsModal from '../IRDeviceSettingsModal.svelte';
	import GatewayG3MainControl from 'src/components/DevicesSettings/VirtualComponents/BluTRV/GatewayG3MainControl.svelte';
	import {
		ActionsContainer,
		AdvancedCardV2,
		AlertV2,
		ButtonV2,
		Container,
		IconButton,
		Modal,
		Text,
	} from '@Shelly/shelly-design-system';
	import { real_id } from 'src/helpers/interface_helpers';
	import {
		deleteIRDevice,
		getIRChildDeviceId,
		getIRDevices,
		IR_DEVICE_TYPE,
		isIRDevice,
		type IRDeviceItem,
	} from 'src/helpers/ir_controller_helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getDeviceChannelByComponent } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';

	export let device: Device;

	let showAddDeviceModal = false;
	let showTrvAssociationsModal = false;
	let showDeviceSettingsModal = false;
	let showDeleteConfirm = false;
	let selectedIRDeviceId: number | null = null;
	let deviceToDelete: Device | null = null;
	let waitForResponse = false;

	const deviceConfig = devices_config?.[device?.type];

	$: status = $devices_status[real_id(device?.id)];
	$: settings = $devices_settings[real_id(device?.id)];
	$: irDevices = getIRDevices(settings);
	$: labelList = (() => {
		if (!status?.online || !deviceConfig) return [];
		const items = status_configurator(status, device, settings);
		return (Array.isArray(items) ? items : []).filter(
			(item) => item.main === undefined && item.name !== 'state',
		);
	})();

	const handleOpenSettings = (irDeviceId: number) => {
		selectedIRDeviceId = irDeviceId;
		showDeviceSettingsModal = true;
	};

	const handleOpenIRDevice = (irDeviceId: number) => {
		const childId = getIRChildDeviceId(device.id, irDeviceId);
		if (isIRDevice($devices[childId]?.type)) {
			setSidePanelState({ type: 'device', id: childId, open: true });
			return;
		}
		handleOpenSettings(irDeviceId);
	};

	const resolveIRDevice = (irDevice: IRDeviceItem): Device => {
		const childId = getIRChildDeviceId(device.id, irDevice.id);
		return (
			$devices[childId] || {
				...device,
				id: childId,
				channel: getDeviceChannelByComponent(irDevice.key),
				component: irDevice.key,
				type: IR_DEVICE_TYPE,
				name: irDevice.name,
			}
		);
	};

	const handleOpenDelete = (irDevice: Device) => {
		deviceToDelete = irDevice;
		showDeleteConfirm = true;
	};

	const handleCloseDelete = () => {
		showDeleteConfirm = false;
		deviceToDelete = null;
	};

	const handleDelete = async () => {
		if (!deviceToDelete || waitForResponse) return;
		waitForResponse = true;
		const response: SettingRequest = await deleteIRDevice(deviceToDelete);
		onSettingsResponse(response, device, {
			success: $format('ir_controller_delete_remote_success', {
				default: 'IR device deleted.',
			}),
		});
		waitForResponse = false;
		if (response?.isok) handleCloseDelete();
	};
</script>

<DeviceViewStateWrapper {device}>
	<div
		slot="staticTop"
		class="label-list-wrapper mb-s d-flex flex-direction-column justify-content-center"
	>
		{#if labelList.length}
			<DeviceLabelList {labelList} />
		{/if}
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>
	<div slot="content">
		{#if irDevices.length === 0}
			<AlertV2
				class="mb-s"
				isHeadingBold
				heading={$format('ir_controller_no_remotes_title', {
					default: 'No remotes yet',
				})}
			/>
		{:else}
			{#each irDevices as irDevice (irDevice.key)}
				{@const irChildDevice = resolveIRDevice(irDevice)}
				<AdvancedCardV2
					class="mb-s"
					title={irDevice.name}
					subtitle={irDevice.codes.length
						? {
								value: irDevice.codes.map((code) => code.name).join(', '),
								type: 'muted',
								bold: false,
								textSmall: true,
							}
						: undefined}
					avatar={{ icon: 'fas fa-remote' }}
					isFocusable={true}
					aria-label={irDevice.name}
					on:click={() => handleOpenIRDevice(irDevice.id)}
				>
					<svelte:fragment slot="statusBar">
						<StatusBar device={irChildDevice} {status} {settings} />
					</svelte:fragment>
					<div
						slot="right"
						class="d-flex align-items-center"
						role="presentation"
						on:click|stopPropagation
						on:keydown|stopPropagation
					>
						<ActionsContainer orientation="horizontal" type="simple">
							<IconButton
								slot="first"
								variant="help-2"
								icon="fas fa-pen"
								aria-label={$format('button_edit', { default: 'Edit' })}
								on:click={() => handleOpenSettings(irDevice.id)}
							/>
							<IconButton
								slot="second"
								variant="help-2"
								icon="fas fa-trash"
								disabled={!status?.online || waitForResponse}
								aria-label={$format('button_delete', { default: 'Delete' })}
								on:click={() => handleOpenDelete(irChildDevice)}
							/>
						</ActionsContainer>
					</div>
				</AdvancedCardV2>
			{/each}
		{/if}
		<GatewayG3MainControl
			{device}
			showAddButton={false}
			bind:showAssociationsModal={showTrvAssociationsModal}
		/>
	</div>
	<div class="flex-full-height justify-content-end" slot="staticBottom">
		<div class="d-flex flex-direction-column gap-s width-full">
			<ButtonV2
				size="m"
				width="full"
				variant="primary"
				disabled={!status?.online}
				aria-label={$format('text_blu_trv_add_new', {
					default: 'Add New TRV Device',
				})}
				on:click={() => (showTrvAssociationsModal = true)}
			>
				{$format('text_blu_trv_add_new', {
					default: 'Add New TRV Device',
				})}
			</ButtonV2>
			<ButtonV2
				size="m"
				width="full"
				disabled={!status?.online}
				aria-label={$format('ir_controller_add_ir_device', { default: 'Add IR Device' })}
				on:click={() => (showAddDeviceModal = true)}
			>
				{$format('ir_controller_add_ir_device', { default: 'Add IR Device' })}
			</ButtonV2>
		</div>
	</div>
</DeviceViewStateWrapper>

<IRAddDeviceModal {device} bind:showModal={showAddDeviceModal} />

{#if selectedIRDeviceId !== null}
	<IRDeviceSettingsModal
		{device}
		irDeviceId={selectedIRDeviceId}
		irDeviceName={irDevices.find((irDevice) => irDevice.id === selectedIRDeviceId)?.name || ''}
		bind:showModal={showDeviceSettingsModal}
	/>
{/if}

<Modal
	showModal={showDeleteConfirm}
	title={$format('ir_controller_delete_ir_device', { default: 'Delete IR device' })}
	on:close={handleCloseDelete}
>
	<Container slot="content" padding="0">
		<Text>
			{$format('ir_controller_delete_ir_device_confirm', {
				default: 'This will delete the device and all its learned codes. Are you sure?',
			})}
		</Text>
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			size="m"
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			on:click={handleCloseDelete}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			size="m"
			variant="danger"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			loading={waitForResponse}
			aria-label={$format('button_delete', { default: 'Delete' })}
			on:click={handleDelete}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</Modal>
